import React, { useState } from 'react';
import { Image, Button, Space, Typography } from 'antd';
import { LeftOutlined, RightOutlined, EyeOutlined } from '@ant-design/icons';
import { getAllImages, getPlaceholderImage } from '../../utils/images';

const { Text } = Typography;

const ImageCarousel = ({
    item,
    size = 'medium',
    height = 200,
    showIndicators = true,
    showArrows = true,
    showPreview = true,
    autoplay = false,
    className = '',
    onImageClick,
    maxImages = 5 // Maximum number of images to show in carousel
}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const allImages = getAllImages(item, size);
    
    // Debug logging
    console.log('ImageCarousel Debug:', {
        item: item,
        allImages: allImages,
        displayImages: allImages.slice(0, maxImages),
        maxImages: maxImages
    });
    
    // Limit the number of images shown
    const displayImages = allImages.slice(0, maxImages);
    
    if (displayImages.length === 0) {
        return (
            <div 
                className={`flex items-center justify-center bg-gray-100 ${className}`}
                style={{ height: `${height}px` }}
            >
                <Image
                    src={getPlaceholderImage()}
                    alt="No image available"
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        objectPosition: 'center',
                        backgroundColor: '#ffffff'
                    }}
                    preview={false}
                />
            </div>
        );
    }
    
    if (displayImages.length === 1) {
        return (
            <div className={`relative ${className}`} style={{ height: `${height}px` }}>
                <Image
                    src={displayImages[0].url}
                    alt={displayImages[0].name || 'Menu item'}
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        objectPosition: 'center',
                        backgroundColor: '#ffffff'
                    }}
                    preview={showPreview}
                    fallback={getPlaceholderImage()}
                    onClick={() => onImageClick?.(displayImages[0], 0)}
                />
            </div>
        );
    }
    
    // Auto-play functionality
    React.useEffect(() => {
        if (autoplay && displayImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % displayImages.length);
            }, 3000); // Change slide every 3 seconds
            
            return () => clearInterval(interval);
        }
    }, [autoplay, displayImages.length]);

    return (
        <div className={`relative group ${className}`} style={{ height: `${height}px`, backgroundColor: '#000000' }}>
            {/* Simple carousel without Ant Design Carousel */}
            <div className="relative w-full h-full overflow-hidden" style={{ backgroundColor: '#000000' }}>
                {displayImages.map((image, index) => (
                    <div 
                        key={image.id || index} 
                        className="absolute inset-0 transition-all duration-500 ease-in-out"
                        style={{
                            transform: `translateX(${(index - currentSlide) * 100}%)`,
                            zIndex: index === currentSlide ? 2 : 1,
                            backgroundColor: '#000000'
                        }}
                    >
                        <Image
                            src={image.url}
                            alt={image.name || `Menu item ${index + 1}`}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain',
                                objectPosition: 'center',
                                backgroundColor: '#ffffff'
                            }}
                            preview={showPreview}
                            fallback={getPlaceholderImage()}
                            onClick={() => onImageClick?.(image, index)}
                        />
                    </div>
                ))}
            </div>
            
            {/* Custom Navigation Arrows */}
            {showArrows && displayImages.length > 1 && (
                <>
                    <Button
                        type="text"
                        icon={<LeftOutlined />}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-70 hover:opacity-100 transition-opacity"
                        onClick={() => {
                            const prevSlide = currentSlide === 0 ? displayImages.length - 1 : currentSlide - 1;
                            setCurrentSlide(prevSlide);
                        }}
                    />
                    <Button
                        type="text"
                        icon={<RightOutlined />}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-md opacity-70 hover:opacity-100 transition-opacity"
                        onClick={() => {
                            const nextSlide = currentSlide === displayImages.length - 1 ? 0 : currentSlide + 1;
                            setCurrentSlide(nextSlide);
                        }}
                    />
                </>
            )}
            
            {/* Dots Indicator */}
            {showIndicators && displayImages.length > 1 && (
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 z-10">
                    {displayImages.map((_, index) => (
                        <button
                            key={index}
                            className={`w-2 h-2 rounded-full transition-all ${
                                index === currentSlide 
                                    ? 'bg-white shadow-md' 
                                    : 'bg-white/50 hover:bg-white/70'
                            }`}
                            onClick={() => setCurrentSlide(index)}
                        />
                    ))}
                </div>
            )}
            
            {/* Image Counter */}
            {displayImages.length > 1 && (
                <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                    {currentSlide + 1} / {displayImages.length}
                </div>
            )}
            
            {/* Multiple Images Indicator */}
            {allImages.length > maxImages && (
                <div className="absolute bottom-2 left-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                    +{allImages.length - maxImages} more
                </div>
            )}
        </div>
    );
};

export default ImageCarousel;
